/**
 * Filtros del inbox — v2.1.
 *
 * Geometría nueva: card-surface (rounded-xl + border + shadow-sm) en lugar
 * de la franja densa anterior. Inputs rounded-md, selects rounded-md,
 * badges/pills rounded-full. Acento magenta sólo en checkbox y SLA vencido.
 */
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/cn";
import type { ConversationFilters } from "@/hooks/use-conversations";

interface FiltersProps {
  value: ConversationFilters;
  onChange: (v: ConversationFilters) => void;
  totalCount: number;
  overdueCount: number;
  /** Cuando estamos dentro de /c/:slug el selector de cliente es redundante. */
  hideClientFilter?: boolean;
}

const STATES = [
  { value: "all", label: "Todos los estados" },
  { value: "bot", label: "Bot" },
  { value: "human", label: "Humano" },
  { value: "escalated", label: "Escaladas" },
  { value: "closed", label: "Cerradas" },
];

const CLIENTS = [
  { value: "all", label: "Todos los clientes" },
  { value: "huggies-py", label: "Huggies PY" },
  { value: "huggies-uy", label: "Huggies UY" },
  { value: "plenitud-uy", label: "Plenitud UY" },
  { value: "qxtel", label: "QXTEL" },
  { value: "champe-agency", label: "Champe Agency" },
];

const PLATFORMS = [
  { value: "all", label: "Todos los canales" },
  { value: "instagram", label: "Instagram" },
  { value: "facebook", label: "Facebook" },
  { value: "whatsapp", label: "WhatsApp" },
];

export function InboxFilters({
  value,
  onChange,
  totalCount,
  overdueCount,
  hideClientFilter,
}: FiltersProps) {
  return (
    <div className="card-surface p-4 flex flex-col gap-3">
      {/* Search */}
      <div className="relative">
        <Search
          className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
          size={14}
          strokeWidth={2}
        />
        <Input
          placeholder="Buscar conversaciones…"
          value={value.search ?? ""}
          onChange={(e) => onChange({ ...value, search: e.target.value })}
          className="pl-9 h-9 text-sm rounded-md bg-surface-2 border-default text-text-primary placeholder:text-text-muted focus:border-magenta"
          aria-label="Buscar conversaciones"
        />
      </div>

      {/* Selects en grilla — flex-wrap para que en angosto se vayan apilando */}
      <div
        className={cn(
          "grid grid-cols-1 gap-2",
          hideClientFilter ? "sm:grid-cols-2" : "sm:grid-cols-3",
        )}
      >
        <Select
          value={value.state ?? "all"}
          onChange={(e) =>
            onChange({
              ...value,
              state: e.target.value as ConversationFilters["state"],
            })
          }
          options={STATES}
          className="h-8 text-xs rounded-md bg-surface-2 border-default text-text-primary normal-case font-sans tracking-normal"
          aria-label="Filtrar por estado"
        />
        {!hideClientFilter && (
          <Select
            value={value.client ?? "all"}
            onChange={(e) =>
              onChange({
                ...value,
                client: e.target.value as ConversationFilters["client"],
              })
            }
            options={CLIENTS}
            className="h-8 text-xs rounded-md bg-surface-2 border-default text-text-primary normal-case font-sans tracking-normal"
            aria-label="Filtrar por cliente"
          />
        )}
        <Select
          value={value.platform ?? "all"}
          onChange={(e) =>
            onChange({
              ...value,
              platform: e.target.value as ConversationFilters["platform"],
            })
          }
          options={PLATFORMS}
          className="h-8 text-xs rounded-md bg-surface-2 border-default text-text-primary normal-case font-sans tracking-normal"
          aria-label="Filtrar por canal"
        />
      </div>

      {/* Footer: checkbox + counters */}
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={value.slaOverdueOnly ?? false}
            onChange={(e) =>
              onChange({ ...value, slaOverdueOnly: e.target.checked })
            }
            className="w-4 h-4 rounded-sm accent-magenta cursor-pointer"
          />
          <span className="font-sans text-xs text-text-secondary">
            Sólo SLA vencido
          </span>
        </label>
        <div className="flex items-center gap-1.5">
          <span className="inline-flex items-center rounded-full border border-default bg-surface-2 px-2.5 py-0.5 text-xs font-sans text-text-secondary">
            <span className="font-mono tabular-nums mr-1">{totalCount}</span>
            en vista
          </span>
          {overdueCount > 0 && (
            <span
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5",
                "text-xs font-sans font-medium",
                "bg-warning-bg text-warning-text border border-warning-border",
              )}
            >
              <span className="font-mono tabular-nums mr-1">{overdueCount}</span>
              vencidas
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
